import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "map-layer-settings";

export type MapLayerKey =
  | "waterSupply"
  | "objects"
  | "pois"
  | "closures"
  | "equipment";

export type MapLayerSetting = {
  readonly enabled: boolean;
  readonly minZoom: number;
  readonly maxZoom: number;
};

export type MapLayerSettings = Partial<Record<MapLayerKey, MapLayerSetting>>;

// Zoom-Schwellen, die gelten, solange ein Layer im neuen Datenportal keine
// eigenen minZoom/maxZoom-Werte hinterlegt hat.
export const defaultZoomRange: Record<
  MapLayerKey,
  { minZoom: number; maxZoom: number }
> = {
  waterSupply: { minZoom: 16, maxZoom: Infinity },
  objects: { minZoom: 13, maxZoom: Infinity },
  pois: { minZoom: 16, maxZoom: Infinity },
  closures: { minZoom: -Infinity, maxZoom: Infinity },
  equipment: { minZoom: -Infinity, maxZoom: Infinity },
};

function useMapLayerSettingsSyncModule(): SyncModule<MapLayerSettings | null> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Map layer settings are a new-Datenportal-only resource — the legacy
      // portal has no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchMapLayerSettingsNew(portalKey, baseUrl)
        : async () => null,
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchMapLayerSettingsNew(
  portalKey: string,
  baseUrl: string,
): Promise<MapLayerSettings | null> {
  const response = await fetch(
    `${baseUrl}/api/device/sync/map-layer-settings`,
    { headers: { Authorization: `Bearer ${portalKey}` } },
  );

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  const settings: MapLayerSettings | null = data.mapLayerSettings ?? null;
  if (!settings) return null;

  return Object.fromEntries(
    Object.entries(settings)
      .filter(
        ([key, setting]) =>
          Object.hasOwn(defaultZoomRange, key) &&
          typeof setting === "object" &&
          setting !== null,
      )
      .map(([key, setting]) => {
        const range = defaultZoomRange[key as MapLayerKey];
        return [
          key,
          {
            ...setting,
            minZoom: setting.minZoom ?? range.minZoom,
            maxZoom: setting.maxZoom ?? range.maxZoom,
          },
        ];
      }),
  ) as MapLayerSettings;
}

export function useMapLayerSettingsSync() {
  const module = useMapLayerSettingsSyncModule();
  useSyncModuleRegistration(module);
}

export function useMapLayerSettings(): MapLayerSettings | null {
  return useSyncData(syncModuleId) ?? null;
}
